import React, { useRef, useState } from 'react';
import type { SourceFileHandle } from '../utils/sourceFileAccess';
import { openPdfWithHandle, supportsNativeFilePicker } from '../utils/sourceFileAccess';
import { validatePdfUploadFile } from '../utils/pdfUploadValidation';

interface PdfUploaderProps {
  onFileSelected: (file: File, sourceHandle?: SourceFileHandle | null) => void;
  disabled?: boolean;
  currentFileName?: string;
  /** Called instead of alert() when an error occurs. Falls back to console.error if omitted. */
  onError?: (msg: string) => void;
}

export const PdfUploader: React.FC<PdfUploaderProps> = ({
  onFileSelected,
  disabled,
  currentFileName,
  onError,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [opening, setOpening] = useState(false);
  const canOpenNative = supportsNativeFilePicker();

  const handleClick = () => {
    if (!disabled) inputRef.current?.click();
  };

  const handleOpenNative = async () => {
    if (disabled || opening) return;
    setOpening(true);
    try {
      const result = await openPdfWithHandle();
      if (result) {
        onFileSelected(result.file, result.handle);
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      const msg = err instanceof Error ? err.message : 'Could not open PDF';
      // Use provided error handler; fall back to console.error (never alert).
      if (onError) {
        onError(msg);
      } else {
        console.error(err);
      }
    } finally {
      setOpening(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validationError = validatePdfUploadFile(file);
      if (validationError) {
        if (onError) {
          onError(validationError);
        } else {
          console.error(validationError);
        }
      } else {
        onFileSelected(file, null);
      }
    }
    e.target.value = '';
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={handleChange}
        disabled={disabled}
      />
      {canOpenNative && (
        <button
          onClick={() => { void handleOpenNative(); }}
          disabled={disabled || opening}
          className="inline-flex items-center gap-2 rounded-lg bg-accent px-4 py-2.5 text-sm font-semibold text-white hover:bg-accent disabled:bg-slate-400 transition-colors"
          title="Open a PDF from your computer — signed files can be saved in the same folder"
        >
          {opening ? 'Opening…' : currentFileName ? 'Open other PDF…' : 'Open PDF…'}
        </button>
      )}
      <button
        onClick={handleClick}
        disabled={disabled}
        className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:bg-slate-400 transition-colors"
        title={canOpenNative ? 'Browse without linking a folder for save' : 'Upload a PDF'}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v-4m0 0l4 4m-4-4l4-4m12 4v4m0 0l-4-4m4 4l-4-4" />
        </svg>
        {currentFileName ? 'Browse…' : canOpenNative ? 'Browse instead' : 'Upload PDF'}
      </button>
      {currentFileName && (
        <span className="text-sm text-slate-600 truncate max-w-[240px] inline-block align-middle" title={currentFileName}>
          {currentFileName}
        </span>
      )}
    </div>
  );
};